import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { CALCOM_DEMO_URL } from "../config/demo";
import { getAuthSignInURL, getAuthSignUpURL, SIGNIN_EVENT, SIGNUP_EVENT } from "../config/auth";


const NAV_ITEMS = [
  { label: "Features", href: "/features" },
  { label: "Pricing", href: "/plans" },
  { label: "How It Works", href: "/how-it-works" },
  {
    label: "Solutions",
    id: "solutions",
    dropdown: [
      { label: "For Cleaning Businesses", href: "/for-cleaning" },
      { label: "For Lawn Care", href: "/for-lawn-care" },
      { label: "For Pest Control", href: "/for-pest-control" },
    ],
  },
  // LAUNCH HIDDEN: Compare dropdown re-enabled when comparison pages go live
  // {
  //   label: "Compare",
  //   id: "compare",
  //   dropdown: [
  //     { label: "CleanBid vs Jobber", href: "/vs-jobber" },
  //     { label: "CleanBid vs Housecall Pro", href: "/vs-housecall-pro" },
  //   ],
  // },
  { label: "About", href: "/about" },
];

const DropdownMenu = ({ item, pathname }) => {
  const [open, setOpen] = useState(false);
  const leaveRef = useRef(null);
  const enter = () => { clearTimeout(leaveRef.current); setOpen(true); };
  const leave = () => { leaveRef.current = setTimeout(() => setOpen(false), 150); };
  const isActive = item.dropdown.some((d) => d.href === pathname);

  return (
    <div className="relative" onMouseEnter={enter} onMouseLeave={leave}>
      <button
        data-testid={`nav-dropdown-${item.id}`}
        className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
          isActive ? "text-[#F4F4F8]" : "text-[#8B8BA7] hover:text-[#F4F4F8] hover:bg-white/5"
        }`}
      >
        {item.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50 min-w-[220px]"
          onMouseEnter={enter}
          onMouseLeave={leave}
        >
          <div className="bg-[#111118] border border-[#1E1E2E] rounded-xl p-1.5 shadow-[0_20px_40px_-10px_rgba(0,0,0,0.7)]">
            {item.dropdown.map((sub) => (
              <Link
                key={sub.href}
                to={sub.href}
                className={`flex items-center px-3 py-2.5 rounded-lg text-sm transition-colors duration-150 ${
                  pathname === sub.href
                    ? "bg-white/5 text-[#F4F4F8]"
                    : "text-[#8B8BA7] hover:text-[#F4F4F8] hover:bg-white/5"
                }`}
              >
                {sub.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileExpanded(null);
  }, [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0A0F]/80 border-b border-[#1E1E2E]"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-[68px]">
          {/* Logo — CleanBid symbol + wordmark lockup */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 flex-shrink-0">
            <img
              src="/cleanbid-logo-transparent.png"
              alt="CleanBid.ai"
              className="h-9 md:h-11 w-auto transition-opacity duration-200 hover:opacity-80"
            />
            <span className="text-lg md:text-xl font-bold tracking-tight text-[#F5F7FA] leading-none">
              CleanBid<span className="text-[#71DED6]">.ai</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV_ITEMS.map((item) =>
              item.dropdown ? (
                <DropdownMenu key={item.label} item={item} pathname={pathname} />
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
                    pathname === item.href
                      ? "text-[#F4F4F8]"
                      : "text-[#8B8BA7] hover:text-[#F4F4F8] hover:bg-white/5"
                  }`}
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <Link
              to={getAuthSignInURL()}
              data-testid="nav-signin-link"
              data-event={SIGNIN_EVENT}
              className="text-sm font-medium text-[#8B8BA7] hover:text-[#F4F4F8] transition-colors duration-200 px-2"
            >
              Sign In
            </Link>
            <Link
              to={getAuthSignUpURL()}
              data-testid="nav-signup-button"
              data-event={SIGNUP_EVENT}
              className="text-sm font-medium text-[#F4F4F8] border border-white/30 rounded-lg px-4 py-2 hover:bg-white/10 transition-colors duration-200"
            >
              Sign Up
            </Link>
            <a
              href={CALCOM_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta-button"
              className="bg-[#71DED6] text-[#10151D] font-semibold px-5 py-2.5 rounded-full text-sm hover:scale-[1.02] hover:shadow-glow-green-sm transition-transform duration-200 inline-block"
            >
              Book a Demo
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-[#8B8BA7] hover:text-[#F4F4F8] transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#111118] border-t border-[#1E1E2E] px-4 py-4 space-y-1 max-h-[80vh] overflow-y-auto"
        >
          {NAV_ITEMS.map((item) =>
            item.dropdown ? (
              <div key={item.label}>
                <button
                  onClick={() =>
                    setMobileExpanded(mobileExpanded === item.label ? null : item.label)
                  }
                  data-testid={`mobile-nav-${item.id}`}
                  className="w-full flex items-center justify-between px-4 py-3 text-[#8B8BA7] hover:text-[#F4F4F8] rounded-xl text-sm font-medium transition-colors"
                >
                  {item.label}
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      mobileExpanded === item.label ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {mobileExpanded === item.label && (
                  <div className="ml-4 mt-1 space-y-0.5">
                    {item.dropdown.map((sub) => (
                      <Link
                        key={sub.href}
                        to={sub.href}
                        className="block px-4 py-2.5 text-[#8B8BA7] hover:text-[#F4F4F8] hover:bg-white/5 rounded-xl text-sm transition-colors"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.href}
                to={item.href}
                className="block px-4 py-3 text-[#8B8BA7] hover:text-[#F4F4F8] hover:bg-white/5 rounded-xl transition-colors text-sm font-medium"
              >
                {item.label}
              </Link>
            )
          )}
          <div className="pt-2 space-y-2">
            <div className="flex gap-2">
              <Link
                to={getAuthSignInURL()}
                data-testid="mobile-signin-link"
                data-event={SIGNIN_EVENT}
                className="flex-1 text-center text-sm font-medium text-[#8B8BA7] hover:text-[#F4F4F8] border border-[#1E1E2E] px-4 py-3 rounded-xl transition-colors"
              >
                Sign In
              </Link>
              <Link
                to={getAuthSignUpURL()}
                data-testid="mobile-signup-button"
                data-event={SIGNUP_EVENT}
                className="flex-1 text-center text-sm font-medium text-[#F4F4F8] border border-white/30 px-4 py-3 rounded-xl hover:bg-white/10 transition-colors"
              >
                Sign Up
              </Link>
            </div>
            <a
              href={CALCOM_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-cta-button"
              className="block w-full text-center bg-[#71DED6] text-[#10151D] font-semibold px-5 py-3 rounded-full text-sm"
            >
              Book a Demo
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
